/**
 * StatsPanel — tableau de bord statistiques dans l'accueil formateur.
 *
 * Fix 2026-09-02 : palette sobre (slate + un accent bleu), style Facebook
 * administratif (pas d'ambre, pas de gradients, icônes lucide simples,
 * cartes blanches/gris subtil, sans emoji).
 *
 * Contenu :
 * - 4 KPI de synthèse (classes, étudiants, taux succès, moy générale)
 * - Bar chart comparaison classes (taux de succès par classe)
 * - Bar chart Top 5 matières (taux succès devoirs)
 * - Line chart distribution des notes (globale ou par matière sélectionnée)
 * - Sélecteur semestre + sélecteur matière
 */
import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, Legend,
} from 'recharts';
import {
  Users, GraduationCap, TrendingUp, BookOpen, Loader2,
  Award, ChevronRight,
} from 'lucide-react';
import { useMesStatistiquesQuery, type StatsMatiereItem } from '../../services/formateurApi';

const BUCKETS = ['0-5', '5-8', '8-10', '10-12', '12-14', '14-16', '16-20'];

// Palette : bleu institutionnel + accents chaleureux (fix "interface triste")
const COLOR_BAR_ADMIS = '#2563eb';       // blue-600 (admis)
const COLOR_BAR_ECHOUE = '#f59e0b';      // amber-500 (à travailler)
const COLOR_BAR_TOP = '#059669';         // emerald-600 (excellence)

interface Props {
  compact?: boolean;   // Version resserrée dans l'accueil ; false = page pleine
}

export function StatsPanel({ compact = false }: Props) {
  const [semestre, setSemestre] = useState<'all' | 'S1' | 'S2'>('all');
  const [matiereSel, setMatiereSel] = useState<number | null>(null);

  const { data, isLoading } = useMesStatistiquesQuery(
    semestre === 'all' ? undefined : { semestre }
  );

  const distGlobale = useMemo(() => {
    if (!data) return BUCKETS.map(b => ({ bucket: b, notes: 0 }));
    if (matiereSel) {
      const m = data.matieres.find(x => x.matiere_id === matiereSel);
      if (m) {
        return BUCKETS.map(b => ({
          bucket: b,
          notes: (m.distribution_devoir[b] || 0) + (m.distribution_examen[b] || 0),
        }));
      }
    }
    // Somme globale
    return BUCKETS.map(b => {
      const total = data.matieres.reduce((acc, m) => acc + (m.distribution_devoir[b] || 0) + (m.distribution_examen[b] || 0), 0);
      return { bucket: b, notes: total };
    });
  }, [data, matiereSel]);

  const comparaison = useMemo(() => {
    if (!data) return [];
    return data.comparaison_classes.map(c => ({
      classe: c.classe,
      taux: c.taux_succes_moyen,
      moy: c.moy_classe,
      etu: c.nb_etudiants,
    }));
  }, [data]);

  const topMatieres = useMemo(() => {
    if (!data) return [];
    return data.top_matieres.slice(0, 5).map((m: StatsMatiereItem, i) => ({
      code: m.code_matiere,
      nom: m.nom_matiere.length > 24 ? m.nom_matiere.slice(0, 22) + '…' : m.nom_matiere,
      taux: m.taux_succes_devoir,
      moy: m.moy_classe_devoir,
      rang: i + 1,
    }));
  }, [data]);

  if (isLoading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-slate-500 text-sm">
        <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement des statistiques…
      </div>
    );
  }

  if (!data || data.matieres.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
        <BookOpen size={24} className="inline text-slate-300 mb-2" />
        <div className="text-sm text-slate-600 font-medium">Aucune donnée de note pour vos classes</div>
        <div className="text-xs text-slate-400 mt-1">Les statistiques apparaîtront dès que des notes seront saisies.</div>
      </div>
    );
  }

  const totaux = data.totaux;

  return (
    <div className="space-y-3">
      {/* Barre de filtres */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mr-1">Semestre</span>
        {(['all', 'S1', 'S2'] as const).map(s => (
          <button key={s}
            onClick={() => setSemestre(s)}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded border transition ${
              semestre === s
                ? 'bg-slate-900 border-slate-900 text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
            }`}>
            {s === 'all' ? 'Tous' : s}
          </button>
        ))}

        {data.matieres.length > 1 && (
          <>
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 ml-3 mr-1">Focus matière</span>
            <select value={matiereSel ?? ''}
              onChange={e => setMatiereSel(e.target.value ? Number(e.target.value) : null)}
              className="px-2 py-1 text-[11px] bg-white border border-slate-200 rounded text-slate-700 max-w-[180px]">
              <option value="">Toutes</option>
              {data.matieres.map(m => (
                <option key={m.matiere_id} value={m.matiere_id}>
                  {m.code_matiere} — {m.classe}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* KPI cards — palette colorée par domaine */}
      <div className={compact ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 md:grid-cols-4 gap-2'}>
        <KpiCard icon={<GraduationCap size={14} />} label="Classes animées"
          value={totaux.nb_classes} accent="indigo" />
        <KpiCard icon={<Users size={14} />} label="Étudiants"
          value={totaux.nb_etudiants_total} accent="blue" />
        <KpiCard icon={<TrendingUp size={14} />}
          label="Taux réussite (devoir)"
          value={`${totaux.taux_succes_devoir}%`}
          accent={totaux.taux_succes_devoir >= 60 ? 'emerald' : 'amber'}
        />
        <KpiCard icon={<Award size={14} />}
          label="Moy. générale"
          value={totaux.moy_globale_devoir?.toFixed(2) || '—'}
          accent={totaux.moy_globale_devoir >= 12 ? 'emerald' : totaux.moy_globale_devoir >= 10 ? 'blue' : 'amber'}
        />
      </div>

      {/* Ligne : comparaison classes + top matières */}
      <div className={compact ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-2 gap-3'}>
        {/* Comparaison classes */}
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Taux de succès par classe
            </h4>
            <span className="text-[10px] text-slate-400">Devoir</span>
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparaison} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="classe" tick={{ fontSize: 10, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} unit="%" />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', fontSize: 11 }}
                  formatter={(v: any) => [`${v}%`, 'Taux']}
                />
                <Bar dataKey="taux" radius={[3, 3, 0, 0]}>
                  {comparaison.map((c, i) => (
                    <Cell key={i} fill={c.taux >= 60 ? COLOR_BAR_ADMIS : COLOR_BAR_ECHOUE} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {comparaison.length > 0 && (
            <div className="mt-1 text-[10px] text-slate-500">
              Bleu foncé = ≥60% de réussite • Gris = à surveiller
            </div>
          )}
        </div>

        {/* Top matières */}
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Top 5 matières (devoir)
            </h4>
            <span className="text-[10px] text-slate-400">Taux réussite</span>
          </div>
          <div className="space-y-1.5">
            {topMatieres.length === 0 && (
              <div className="text-xs text-slate-400 text-center py-4">
                Aucune matière notée pour l'instant.
              </div>
            )}
            {topMatieres.map(m => (
              <div key={m.code} className="flex items-center gap-2">
                <div className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded text-[10px] font-bold ${
                  m.rang === 1 ? 'bg-slate-900 text-white'
                  : m.rang === 2 ? 'bg-slate-700 text-white'
                  : m.rang === 3 ? 'bg-slate-500 text-white'
                  : 'bg-slate-200 text-slate-700'
                }`}>
                  {m.rang}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-mono text-slate-500 flex-shrink-0">{m.code}</span>
                    <span className="text-xs text-slate-800 truncate">{m.nom}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                    <div className="h-full rounded-full"
                      style={{
                        width: `${Math.min(100, m.taux)}%`,
                        background: m.taux >= 60 ? COLOR_BAR_TOP : COLOR_BAR_ECHOUE,
                      }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-bold text-slate-800">{m.taux}%</div>
                  <div className="text-[9px] text-slate-400">moy. {m.moy.toFixed(1)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Distribution des notes */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
            Distribution des notes {matiereSel ? '(matière sélectionnée)' : '(toutes matières)'}
          </h4>
          <span className="text-[10px] text-slate-400">Devoir + Examen</span>
        </div>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distGlobale} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', fontSize: 11 }}
                formatter={(v: any) => [`${v} note${v > 1 ? 's' : ''}`, 'Effectif']}
              />
              <Bar dataKey="notes" radius={[3, 3, 0, 0]}>
                {distGlobale.map((d, i) => (
                  <Cell key={i}
                    fill={parseInt(d.bucket) >= 10 ? COLOR_BAR_ADMIS : COLOR_BAR_ECHOUE} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-1 flex items-center gap-3 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLOR_BAR_ADMIS }} />
            ≥ 10 (admis)
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: COLOR_BAR_ECHOUE }} />
            &lt; 10 (échec)
          </span>
        </div>
      </div>

      {/* Tableau détaillé (masqué en compact) */}
      {!compact && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200">
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-600">
              Détail par matière
            </h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2" rowSpan={2}>Matière</th>
                  <th className="text-left px-3 py-2" rowSpan={2}>Classe</th>
                  <th className="text-center px-2 py-2" rowSpan={2}>Sem.</th>
                  <th className="text-center px-2 py-2" rowSpan={2}>Étu.</th>
                  <th className="text-center px-2 py-1 border-b border-slate-200" colSpan={3}>Devoir (contrôle continu)</th>
                  <th className="text-center px-2 py-1 border-b border-slate-200 border-l border-slate-200" colSpan={3}>Examen final</th>
                </tr>
                <tr>
                  <th className="text-center px-2 py-1 font-normal normal-case text-[10px] text-slate-500">Notes saisies</th>
                  <th className="text-center px-2 py-1 font-normal normal-case text-[10px] text-slate-500">Moy.</th>
                  <th className="text-center px-2 py-1 font-normal normal-case text-[10px] text-slate-500">Taux ≥10</th>
                  <th className="text-center px-2 py-1 font-normal normal-case text-[10px] text-slate-500 border-l border-slate-200">Notes saisies</th>
                  <th className="text-center px-2 py-1 font-normal normal-case text-[10px] text-slate-500">Moy.</th>
                  <th className="text-center px-2 py-1 font-normal normal-case text-[10px] text-slate-500">Taux ≥10</th>
                </tr>
              </thead>
              <tbody>
                {data.matieres.map(m => (
                  <tr key={`${m.matiere_id}-${m.semestre}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-mono text-[10px] text-slate-500">{m.code_matiere}</div>
                      <div className="text-slate-800 truncate max-w-[220px]">{m.nom_matiere}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{m.classe}</td>
                    <td className="text-center px-2 py-2 text-slate-600">{m.semestre}</td>
                    <td className="text-center px-2 py-2 text-slate-600">{m.nb_etudiants}</td>

                    {/* Devoir */}
                    <td className="text-center px-2 py-2 text-slate-600">
                      {m.nb_notes_devoir > 0 ? (
                        <span><strong>{m.nb_notes_devoir}</strong><span className="text-slate-400"> / {m.nb_etudiants}</span></span>
                      ) : <span className="text-slate-300">0</span>}
                    </td>
                    <td className={`text-center px-2 py-2 font-semibold ${
                      m.nb_notes_devoir === 0 ? 'text-slate-300'
                        : m.moy_classe_devoir >= 10 ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {m.nb_notes_devoir > 0 ? m.moy_classe_devoir.toFixed(2) : '—'}
                    </td>
                    <td className={`text-center px-2 py-2 font-semibold ${
                      m.nb_notes_devoir === 0 ? 'text-slate-300'
                        : m.taux_succes_devoir >= 60 ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {m.nb_notes_devoir > 0 ? `${m.taux_succes_devoir}%` : '—'}
                    </td>

                    {/* Examen */}
                    <td className="text-center px-2 py-2 text-slate-600 border-l border-slate-100">
                      {m.nb_notes_examen > 0 ? (
                        <span><strong>{m.nb_notes_examen}</strong><span className="text-slate-400"> / {m.nb_etudiants}</span></span>
                      ) : <span className="text-slate-300" title="Aucun examen encore saisi">0</span>}
                    </td>
                    <td className={`text-center px-2 py-2 font-semibold ${
                      m.nb_notes_examen === 0 ? 'text-slate-300'
                        : m.moy_classe_examen >= 10 ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {m.nb_notes_examen > 0 ? m.moy_classe_examen.toFixed(2) : '—'}
                    </td>
                    <td className={`text-center px-2 py-2 font-semibold ${
                      m.nb_notes_examen === 0 ? 'text-slate-300'
                        : m.taux_succes_examen >= 60 ? 'text-emerald-700' : 'text-amber-700'
                    }`}>
                      {m.nb_notes_examen > 0 ? `${m.taux_succes_examen}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

type AccentColor = 'blue' | 'emerald' | 'amber' | 'indigo' | 'rose';

function KpiCard({ icon, label, value, accent = 'blue' }: {
  icon: React.ReactNode; label: string; value: string | number; accent?: AccentColor;
}) {
  // Fix 2026-09-02 v4 : plus de border-l colorée — juste un icône coloré dans
  // une pastille + valeur colorée. Bordure normale slate sur tous les côtés.
  const cfg = {
    blue:    { icon: 'text-blue-600 bg-blue-50',       val: 'text-blue-700' },
    emerald: { icon: 'text-emerald-600 bg-emerald-50', val: 'text-emerald-700' },
    amber:   { icon: 'text-amber-600 bg-amber-50',     val: 'text-amber-700' },
    indigo:  { icon: 'text-indigo-600 bg-indigo-50',   val: 'text-indigo-700' },
    rose:    { icon: 'text-rose-600 bg-rose-50',       val: 'text-rose-700' },
  }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider font-semibold">
        <span className={`p-1 rounded ${cfg.icon}`}>{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-lg font-bold mt-1 ${cfg.val}`}>{value}</div>
    </div>
  );
}
